import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import useEmblaCarousel from 'embla-carousel-react';

import api from '../../api';
import { setHeadFeatures } from '../../actions';
import { isCreateYourOwnHeadProductId } from '../../constants';

import FeatureGroup from './FeatureGroup';

/**
 * Parent category configuration for the head builder UI.
 * Maps top-level toggle buttons to their subcategories and feature keys.
 */
const PARENT_CATEGORIES = [
    {
        key: 'eyes',
        label: 'Eyes',
        subcategories: [
            { key: 'eyes', label: 'Eyes', featureKey: 'eyes', optional: false },
            { key: 'eyebrows', label: 'Eyebrows', featureKey: 'eyebrows', optional: false },
            { key: 'glasses', label: 'Glasses', featureKey: 'glasses', optional: true },
        ],
    },
    {
        key: 'mouth',
        label: 'Mouth',
        subcategories: null,
        featureKey: 'mouth',
        optional: false,
    },
    {
        key: 'facialFeatures',
        label: 'Facial Features',
        subcategories: null,
        featureKey: 'facialFeatures',
        optional: true,
    },
    {
        key: 'facialHair',
        label: 'Facial Hair',
        subcategories: [
            { key: 'beard', label: 'Beard', styleKey: 'beardStyles' },
            { key: 'stubble', label: 'Stubble', styleKey: 'stubbleStyles' },
            { key: 'moustache', label: 'Moustache', styleKey: 'moustacheStyles' },
        ],
    },
];

/**
 * Head Builder Options Component
 * Renders facial feature selection interface for "Create your own head" product
 */
const HeadBuilderOptions = ({ product, currentBuildIndex, dispatch, headFeatures }) => {
    const [config, setConfig] = useState(null);
    const [selections, setSelections] = useState({
        eyebrows: null,
        eyes: null,
        mouth: null,
        facialHairType: null,
        facialHairStyle: null,
        glasses: null,
        facialFeatures: null,
    });
    const [activeParent, setActiveParent] = useState('eyes');
    const [activeSubcategory, setActiveSubcategory] = useState('eyes');
    const [parentEmblaRef] = useEmblaCarousel({ align: 'start', dragFree: true });
    const [subEmblaRef] = useEmblaCarousel({ align: 'start', dragFree: true });
    const initializedRef = useRef(false);
    const headFeaturesRef = useRef(headFeatures);
    headFeaturesRef.current = headFeatures;

    // Only show for the custom head product. Its handle can change independently of its ID.
    const shouldRender = isCreateYourOwnHeadProductId(product?.id);

    useEffect(() => {
        if (!shouldRender) {
            setConfig(null);
            return;
        }

        initializedRef.current = false;

        api.getHeadBuilderConfig(product.handle)
            .then((data) => {
                if (data) {
                    setConfig(data);

                    // Helper function to find handle by image URL
                    const findHandleByImageUrl = (featureKey, imageUrl) => {
                        if (!imageUrl) return null;
                        const options = data[featureKey]?.references?.nodes || [];
                        const match = options.find((opt) => opt.image?.reference?.image?.url === imageUrl);
                        return match?.handle || null;
                    };

                    // Read headFeatures from ref to avoid stale closure
                    const currentHeadFeatures = headFeaturesRef.current;

                    // Check if headFeatures already exist (head already in build)
                    const hasExistingHead =
                        currentHeadFeatures &&
                        (currentHeadFeatures.eyebrows || currentHeadFeatures.eyes || currentHeadFeatures.mouth);

                    if (hasExistingHead) {
                        // Restore existing selections by reverse-lookup
                        const restored = {
                            eyebrows: findHandleByImageUrl('eyebrows', currentHeadFeatures.eyebrows),
                            eyes: findHandleByImageUrl('eyes', currentHeadFeatures.eyes),
                            mouth: findHandleByImageUrl('mouth', currentHeadFeatures.mouth),
                            glasses: findHandleByImageUrl('glasses', currentHeadFeatures.glasses),
                            facialFeatures:
                                currentHeadFeatures.facialFeaturesHandle ||
                                findHandleByImageUrl('facialFeatures', currentHeadFeatures.facialFeatures),
                            facialHairType: currentHeadFeatures.facialHair?.type || null,
                            facialHairStyle: currentHeadFeatures.facialHair?.style || null,
                        };

                        setSelections(restored);
                        if (!currentHeadFeatures.facialFeaturesHandle && restored.facialFeatures) {
                            dispatch(
                                setHeadFeatures(currentBuildIndex, {
                                    ...currentHeadFeatures,
                                    facialFeaturesHandle: restored.facialFeatures,
                                })
                            );
                        }
                        // Don't dispatch setHeadFeatures - it's already set in Redux

                        // Restore active parent/subcategory tabs
                        if (restored.facialHairType) {
                            setActiveParent('facialHair');
                            setActiveSubcategory(restored.facialHairType);
                        } else {
                            setActiveParent('eyes');
                            setActiveSubcategory('eyes');
                        }
                    } else {
                        // Set default selections for non-optional features (new head)
                        const defaults = {};
                        const nonOptionalFeatures = ['eyebrows', 'eyes', 'mouth'];

                        nonOptionalFeatures.forEach((featureKey) => {
                            const options = data[featureKey]?.references?.nodes || [];
                            if (options.length > 0) {
                                defaults[featureKey] = options[0].handle;
                            }
                        });

                        setSelections((prev) => ({
                            ...prev,
                            ...defaults,
                        }));

                        const defaultImages = {};
                        nonOptionalFeatures.forEach((featureKey) => {
                            const options = data[featureKey]?.references?.nodes || [];
                            if (options.length > 0) {
                                defaultImages[featureKey] = options[0]?.image?.reference?.image?.url || null;
                            }
                        });

                        const newHeadFeatures = {
                            eyebrows: null,
                            eyes: null,
                            mouth: null,
                            facialHair: {
                                type: null,
                                style: null,
                                styleImageUrl: null,
                            },
                            glasses: null,
                            facialFeatures: null,
                            facialFeaturesHandle: null,
                            ...defaultImages,
                        };
                        dispatch(setHeadFeatures(currentBuildIndex, newHeadFeatures));

                        // Default to first parent/subcategory
                        setActiveParent('eyes');
                        setActiveSubcategory('eyes');
                    }

                    initializedRef.current = true;
                }
            })
            .catch((err) => {
                console.error('[HeadBuilderOptions] Failed to load config:', err);
            });
    }, [shouldRender, product?.handle, currentBuildIndex, dispatch]);

    const handleFeatureChange = (featureKey, value) => {
        setSelections((prev) => ({
            ...prev,
            [featureKey]: value,
        }));

        const options = config[featureKey]?.references?.nodes || [];
        const selectedOption = options.find((opt) => opt.handle === value);
        const imageUrl = selectedOption?.image?.reference?.image?.url || null;

        dispatch((dispatch, getState) => {
            const currentHeadFeatures = getState().builds[getState().currentBuildIndex].headFeatures;
            const updatedFeatures = {
                ...currentHeadFeatures,
                [featureKey]: imageUrl,
                ...(featureKey === 'facialFeatures' ? { facialFeaturesHandle: value } : {}),
            };
            dispatch(setHeadFeatures(currentBuildIndex, updatedFeatures));
        });
    };

    const handleFacialHairTypeChange = (value) => {
        setSelections((prev) => ({
            ...prev,
            facialHairType: value,
            facialHairStyle: null,
        }));

        if (value) {
            const styleKey =
                value === 'beard' ? 'beardStyles' : value === 'stubble' ? 'stubbleStyles' : 'moustacheStyles';
            const styleOptions = config[styleKey]?.references?.nodes || [];
            if (styleOptions.length > 0) {
                const firstStyle = styleOptions[0];
                const imageUrl = firstStyle?.image?.reference?.image?.url || null;

                setSelections((prev) => ({
                    ...prev,
                    facialHairStyle: firstStyle.handle,
                }));

                dispatch((dispatch, getState) => {
                    const currentHeadFeatures = getState().builds[getState().currentBuildIndex].headFeatures;
                    const updatedFeatures = {
                        ...currentHeadFeatures,
                        facialHair: {
                            type: value,
                            style: firstStyle.handle,
                            styleImageUrl: imageUrl,
                        },
                    };
                    dispatch(setHeadFeatures(currentBuildIndex, updatedFeatures));
                });
            }
        } else {
            dispatch((dispatch, getState) => {
                const currentHeadFeatures = getState().builds[getState().currentBuildIndex].headFeatures;
                const updatedFeatures = {
                    ...currentHeadFeatures,
                    facialHair: {
                        type: null,
                        style: null,
                        styleImageUrl: null,
                    },
                };
                dispatch(setHeadFeatures(currentBuildIndex, updatedFeatures));
            });
        }
    };

    const handleFacialHairStyleChange = (value) => {
        const { facialHairType } = selections;
        if (!facialHairType) return;

        setSelections((prev) => ({
            ...prev,
            facialHairStyle: value,
        }));

        const styleKey =
            facialHairType === 'beard'
                ? 'beardStyles'
                : facialHairType === 'stubble'
                  ? 'stubbleStyles'
                  : 'moustacheStyles';
        const styleOptions = config[styleKey]?.references?.nodes || [];
        const selectedOption = styleOptions.find((opt) => opt.handle === value);
        const imageUrl = selectedOption?.image?.reference?.image?.url || null;

        dispatch((dispatch, getState) => {
            const currentHeadFeatures = getState().builds[getState().currentBuildIndex].headFeatures;
            const updatedFeatures = {
                ...currentHeadFeatures,
                facialHair: {
                    type: facialHairType,
                    style: value,
                    styleImageUrl: imageUrl,
                },
            };
            dispatch(setHeadFeatures(currentBuildIndex, updatedFeatures));
        });
    };

    const handleParentClick = (parentKey) => {
        setActiveParent(parentKey);
        const parent = PARENT_CATEGORIES.find((p) => p.key === parentKey);
        if (parent?.subcategories) {
            if (parentKey === 'facialHair') {
                // Restore existing type selection or default to beard
                const defaultType = selections.facialHairType || 'beard';
                setActiveSubcategory(defaultType);
                if (!selections.facialHairType) {
                    handleFacialHairTypeChange(defaultType);
                }
            } else {
                setActiveSubcategory(parent.subcategories[0].key);
            }
        } else {
            setActiveSubcategory(null);
        }
    };

    const handleSubcategoryClick = (parentKey, subKey) => {
        if (parentKey === 'facialHair') {
            setActiveSubcategory(subKey);
            handleFacialHairTypeChange(subKey);
        } else {
            setActiveSubcategory(subKey);
        }
    };

    if (!shouldRender || !config) {
        return null;
    }

    const activeParentConfig = PARENT_CATEGORIES.find((p) => p.key === activeParent);

    const renderActiveFeatureGroup = () => {
        if (!activeParentConfig) return null;

        // Parent without subcategories (Mouth, Facial Features)
        if (!activeParentConfig.subcategories) {
            const options = config[activeParentConfig.featureKey]?.references?.nodes || [];
            return (
                <FeatureGroup
                    key={activeParentConfig.featureKey}
                    title={activeParentConfig.label}
                    options={options}
                    selected={selections[activeParentConfig.featureKey]}
                    onChange={(value) => handleFeatureChange(activeParentConfig.featureKey, value)}
                    optional={activeParentConfig.optional}
                />
            );
        }

        // Facial Hair parent - subcategories control type, show styles
        if (activeParent === 'facialHair') {
            if (!activeSubcategory) return null;
            const sub = activeParentConfig.subcategories.find((s) => s.key === activeSubcategory);
            if (!sub) return null;
            const styleOptions = config[sub.styleKey]?.references?.nodes || [];
            return (
                <FeatureGroup
                    key={`facialHairStyle-${activeSubcategory}`}
                    title={`${sub.label} Style`}
                    options={styleOptions}
                    selected={selections.facialHairStyle}
                    onChange={(value) => {
                        if (value === null) {
                            // "None" clicked - clear facial hair entirely
                            setActiveSubcategory(null);
                            handleFacialHairTypeChange(null);
                        } else {
                            handleFacialHairStyleChange(value);
                        }
                    }}
                    optional={true}
                />
            );
        }

        const sub = activeParentConfig.subcategories.find((s) => s.key === activeSubcategory);
        if (!sub) return null;
        const options = config[sub.featureKey]?.references?.nodes || [];
        return (
            <FeatureGroup
                key={sub.featureKey}
                title={sub.label}
                options={options}
                selected={selections[sub.featureKey]}
                onChange={(value) => handleFeatureChange(sub.featureKey, value)}
                optional={sub.optional}
            />
        );
    };

    return (
        <div className="head-builder-options">
            <h2 className="head-builder-main-title">Customise your head</h2>

            {/* Parent category row */}
            <div className="head-builder-categories">
                <div className="embla" ref={parentEmblaRef}>
                    <div className="embla__container">
                        {PARENT_CATEGORIES.map((parent) => (
                            <div key={parent.key} className="embla__slide">
                                <button
                                    type="button"
                                    className={`head-builder-category-btn${activeParent === parent.key ? ' active' : ''}`}
                                    onClick={() => handleParentClick(parent.key)}
                                >
                                    {parent.label}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Subcategory row (only for parents with subcategories) */}
            {activeParentConfig?.subcategories && (
                <div className="head-builder-subcategories">
                    <div className="embla" ref={subEmblaRef}>
                        <div className="embla__container">
                            {activeParentConfig.subcategories.map((sub) => (
                                <div key={sub.key} className="embla__slide">
                                    <button
                                        type="button"
                                        className={`head-builder-subcategory-btn${activeSubcategory === sub.key ? ' active' : ''}`}
                                        onClick={() => handleSubcategoryClick(activeParent, sub.key)}
                                    >
                                        {sub.label}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Active feature group */}
            {renderActiveFeatureGroup()}

            {/* Hidden form inputs for submission */}
            {Object.entries(selections).map(([key, value]) => {
                // Skip facial hair fields as we handle them separately
                if (key === 'facialHairType' || key === 'facialHairStyle') return null;
                return value ? <input key={key} type="hidden" name={`properties[_head_${key}]`} value={value} /> : null;
            })}
            {selections.facialHairType && (
                <input
                    key="facialHairType"
                    type="hidden"
                    name="properties[_head_facialHairType]"
                    value={selections.facialHairType}
                />
            )}
            {selections.facialHairStyle && (
                <input
                    key="facialHairStyle"
                    type="hidden"
                    name="properties[_head_facialHairStyle]"
                    value={selections.facialHairStyle}
                />
            )}
        </div>
    );
};

HeadBuilderOptions.propTypes = {
    product: PropTypes.shape({
        id: PropTypes.string,
        handle: PropTypes.string,
    }).isRequired,
    activeSection: PropTypes.string.isRequired,
    currentBuildIndex: PropTypes.number.isRequired,
    dispatch: PropTypes.func.isRequired,
    headFeatures: PropTypes.shape({
        eyebrows: PropTypes.string,
        eyes: PropTypes.string,
        mouth: PropTypes.string,
        facialHair: PropTypes.shape({
            type: PropTypes.string,
            style: PropTypes.string,
            styleImageUrl: PropTypes.string,
        }),
        glasses: PropTypes.string,
        facialFeatures: PropTypes.string,
        facialFeaturesHandle: PropTypes.string,
    }).isRequired,
};

const mapStateToProps = (state) => ({
    currentBuildIndex: state.currentBuildIndex,
    headFeatures: state.builds[state.currentBuildIndex].headFeatures,
});

export default connect(mapStateToProps)(HeadBuilderOptions);
